import { LicenceOrigin, LicenceState, SubscriptionState } from 'dy-frontend-http-repository/lib/data/enums';
import {
    ClientUserListItemResource,
    LicenceResource,
} from 'dy-frontend-http-repository/lib/modules/ClientUser/resources';

// Client users are shown as one option per business, so the popup never exposes a person name or an email
export interface ClientCompanyOption {
    key: string;
    label: string;
    clientIds: ID[];
}

// Amount of business names shown on the filter button before the rest is collapsed into a counter
const shownCompanyLabelCount = 2;

// Clients whose business is not filled in get a neutral label instead of any identifying value
const fallbackCompanyLabel = 'Business not provided';

// Case and outer/internal whitespace variants of the same business name belong to the same option
const getCompanyGroupKey = (companyName: string) => companyName.trim().replace(/\s+/g, ' ').toLowerCase();

// IDs are opaque strings, so they are never compared or sorted as numbers
const compareClientIds = (a: ID, b: ID) => (a < b ? -1 : a > b ? 1 : 0);

// Client inventory is collapsed into unique businesses, every option owning all of its client user IDs
export const groupClientsByCompany = (clients: ClientUserListItemResource[]): ClientCompanyOption[] => {
    // Stable ID order keeps labels, fallback numbering and member order independent of the inventory order
    const orderedClients = [...clients].sort((a, b) => compareClientIds(a.id, b.id));

    const companyOptions: ClientCompanyOption[] = [];
    const companyOptionIndexes = new Map<string, number>();
    const clientsWithoutCompany: ClientUserListItemResource[] = [];

    orderedClients.forEach((client) => {
        const groupKey = getCompanyGroupKey(client.company_name ?? '');

        if (groupKey.length === 0) {
            // Business is NOT filled in, so this client can not be merged with any other one
            clientsWithoutCompany.push(client);
            return;
        }

        const existingIndex = companyOptionIndexes.get(groupKey);
        if (existingIndex === undefined) {
            // First trimmed spelling of the business becomes the label of the whole group
            companyOptionIndexes.set(groupKey, companyOptions.length);
            companyOptions.push({
                key: `company:${groupKey}`,
                label: client.company_name.trim(),
                clientIds: [client.id],
            });
        } else {
            companyOptions[existingIndex].clientIds.push(client.id);
        }
    });

    // Workspace titles are only usable when they can not be confused with another option
    const workspaceTitleCounts = new Map<string, number>();
    clientsWithoutCompany.forEach((client) => {
        const workspaceTitleKey = getCompanyGroupKey(client.workspace?.title ?? '');

        if (workspaceTitleKey.length > 0) {
            workspaceTitleCounts.set(workspaceTitleKey, (workspaceTitleCounts.get(workspaceTitleKey) ?? 0) + 1);
        }
    });

    let fallbackCompanyNumber = 0;
    const fallbackOptions = clientsWithoutCompany.map((client) => {
        const workspaceTitle = (client.workspace?.title ?? '').trim();
        const workspaceTitleKey = getCompanyGroupKey(workspaceTitle);
        const isWorkspaceTitleUsable =
            workspaceTitleKey.length > 0 &&
            workspaceTitleCounts.get(workspaceTitleKey) === 1 &&
            !companyOptionIndexes.has(workspaceTitleKey);

        fallbackCompanyNumber += isWorkspaceTitleUsable ? 0 : 1;

        return {
            key: `client:${client.id}`,
            label: isWorkspaceTitleUsable ? workspaceTitle : `${fallbackCompanyLabel} ${fallbackCompanyNumber}`,
            clientIds: [client.id],
        };
    });

    companyOptions.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
    companyOptions.forEach((option) => option.clientIds.sort(compareClientIds));

    // Businesses without a name are kept together at the end, so they never interleave with real ones
    return [...companyOptions, ...fallbackOptions];
};

// Licence is paid right now only when it came from a subscription instead of a manual grant, the
// subscription itself is the one state in which the latest payment succeeded, and the licence is not
// suspended, which is how a paused subscription shows up here. The resource types subscription as
// required, but the API sends null for a manual grant, so the access is guarded
export const checkIfLicenceCurrentlyPaid = (licence: LicenceResource): boolean =>
    licence.origin === LicenceOrigin.SUBSCRIPTION &&
    licence.state === LicenceState.ACTIVE &&
    licence.subscription?.state === SubscriptionState.ACTIVE;

// One currently paid licence is enough, the client keeps every other licence it happens to hold
export const checkIfClientCurrentlyPaid = (client: ClientUserListItemResource): boolean =>
    (client.licences ?? []).some(checkIfLicenceCurrentlyPaid);

// Only businesses with at least one currently paying client user are offered, and every offered
// business keeps all of its members, so selecting it still filters the board business wide
export const selectCompanyOptionsWithPaidSubscription = (
    options: ClientCompanyOption[],
    clients: ClientUserListItemResource[]
): ClientCompanyOption[] => {
    const paidClientIds = clients.filter(checkIfClientCurrentlyPaid).map((client) => client.id);

    return options.filter((option) => option.clientIds.some((clientId) => paidClientIds.includes(clientId)));
};

// Searching narrows the shown businesses by their label only, so no private client field is ever matched
export const filterCompanyOptionsByQuery = (
    options: ClientCompanyOption[],
    query: string
): ClientCompanyOption[] => {
    const normalizedQuery = getCompanyGroupKey(query);

    if (normalizedQuery.length === 0) {
        // Nothing is typed, so the complete inventory is shown in its original order
        return options;
    }

    return options.filter((option) => getCompanyGroupKey(option.label).includes(normalizedQuery));
};

// Business is checked only when every one of its client users is selected
export const isCompanyOptionChecked = (option: ClientCompanyOption, selectedClientIds: ID[]) => {
    return (
        option.clientIds.length > 0 && option.clientIds.every((clientId) => selectedClientIds.includes(clientId))
    );
};

// Hydrating a selection which covers a business only partially leaves that business indeterminate
export const isCompanyOptionIndeterminate = (option: ClientCompanyOption, selectedClientIds: ID[]) => {
    return (
        !isCompanyOptionChecked(option, selectedClientIds) &&
        option.clientIds.some((clientId) => selectedClientIds.includes(clientId))
    );
};

// Toggling a business applies to all of its client users, selected IDs outside the inventory are kept
export const toggleCompanyOptionSelection = (option: ClientCompanyOption, selectedClientIds: ID[]): ID[] => {
    if (isCompanyOptionChecked(option, selectedClientIds)) {
        // Business is fully checked, so clicking it removes every one of its client users
        return selectedClientIds.filter((selectedClientId) => !option.clientIds.includes(selectedClientId));
    }

    // Business is unchecked or indeterminate, so clicking it completes the selection
    const addedClientIds = option.clientIds.filter((clientId) => !selectedClientIds.includes(clientId));

    return [...selectedClientIds, ...addedClientIds];
};

// Filter button shows businesses as well, falling back to a bare count while the inventory is unknown
export const getSelectedCompanyLabel = (options: ClientCompanyOption[], selectedClientIds: ID[]) => {
    if (selectedClientIds.length === 0) {
        return '';
    }

    const selectedCompanyLabels = options
        .filter(
            (option) =>
                isCompanyOptionChecked(option, selectedClientIds) ||
                isCompanyOptionIndeterminate(option, selectedClientIds)
        )
        .map((option) => option.label);

    if (selectedCompanyLabels.length === 0) {
        // Inventory is not loaded yet, so the count is the only privacy safe label
        return `${selectedClientIds.length} selected`;
    }

    const knownClientIds = options.reduce<ID[]>((clientIds, option) => [...clientIds, ...option.clientIds], []);
    const unknownSelectedCount = selectedClientIds.filter(
        (selectedClientId) => !knownClientIds.includes(selectedClientId)
    ).length;

    const hiddenCount =
        Math.max(selectedCompanyLabels.length - shownCompanyLabelCount, 0) + unknownSelectedCount;
    const shownCompanyLabels = selectedCompanyLabels.slice(0, shownCompanyLabelCount).join(', ');

    return hiddenCount > 0 ? `${shownCompanyLabels} +${hiddenCount}` : shownCompanyLabels;
};
